import { memo, MouseEvent, useCallback, useEffect, useState } from 'react'
import { Checkbox } from '@microservices/wiskey-react-components'
import { Box } from '@mui/material'

import { useSelectionContextOptional } from '@pages/ConfiguredEntity/context'
import {
  resolveRowSelectionChecked,
  resolveSelectAllCheckboxChecked,
} from '@pages/ConfiguredEntity/helpers/resolveRowSelectionChecked'

import { CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME } from '@constants'
import { ObjectDataRecord } from '@types'

const selectionCheckboxSx = {
  height: '24px',
  input: {
    zIndex: (theme: { zIndex: { modal: number } }) => theme.zIndex.modal + 15,
  },
  color: (theme: { palette: { color: { ddwCheckbox: string } } }) =>
    theme.palette.color.ddwCheckbox,
  '&.Mui-checked': {
    color: (theme: { palette: { color: { ddwCheckbox: string } } }) =>
      theme.palette.color.ddwCheckbox,
  },
  '&.Mui-disabled': {
    color: (theme: { palette: { grey: Record<string, string> } }) => theme.palette.grey['400'],
    pointerEvents: 'fill',
  },
}

const getRowSelectionId = (row: ObjectDataRecord) =>
  String(row._id ?? row[CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME])

type SelectionCheckboxCellProps = {
  row: ObjectDataRecord
  readonly?: boolean
  isEditMode?: boolean
}

export const SelectionCheckboxCell = memo(function SelectionCheckboxCell({
  row,
  readonly,
  isEditMode,
}: SelectionCheckboxCellProps) {
  const selection = useSelectionContextOptional()
  const rowId = getRowSelectionId(row)
  const checkedFromSelection = selection
    ? resolveRowSelectionChecked(
        selection.bulkSelectionOverlay,
        selection.selectedRowIdsSet,
        rowId
      )
    : false
  const [localChecked, setLocalChecked] = useState(checkedFromSelection)

  useEffect(() => {
    setLocalChecked(checkedFromSelection)
  }, [checkedFromSelection])

  const checked = selection?.bulkSelectionOverlay ? checkedFromSelection : localChecked

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      if (!selection?.bulkSelectionOverlay) {
        setLocalChecked(prev => !prev)
      }
      selection?.onToggleRowSelection(row)
    },
    [selection, row]
  )

  return (
    <Box onClick={event => event.stopPropagation()}>
      <Checkbox
        checked={checked}
        disabled={readonly || isEditMode}
        sx={selectionCheckboxSx}
        onClick={handleClick}
      />
    </Box>
  )
})

type SelectionCheckboxHeaderProps = {
  readonly?: boolean
  isEditMode?: boolean
  multiselection?: boolean
}

export const SelectionCheckboxHeader = memo(function SelectionCheckboxHeader({
  readonly,
  isEditMode,
  multiselection,
}: SelectionCheckboxHeaderProps) {
  const selection = useSelectionContextOptional()
  const checkedFromSelection = selection
    ? resolveSelectAllCheckboxChecked(
        selection.bulkSelectionOverlay,
        selection.isSelectAllCheckbox
      )
    : false

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      selection?.onToggleAllRowsSelection(event)
    },
    [selection]
  )

  return (
    <Box onClick={event => event.stopPropagation()}>
      <Checkbox
        checked={checkedFromSelection}
        disabled={readonly || isEditMode || !multiselection}
        sx={selectionCheckboxSx}
        onClick={handleClick}
      />
    </Box>
  )
})
